/**
 * Admin layout: Topbar + Sidebar + Main content.
 * Design matches site (Add-e SaaS).
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Topbar } from '../components/admin/Topbar';
import { Sidebar } from '../components/admin/Sidebar';
import { ImpersonationBanner } from '../components/admin/ImpersonationBanner';
import './AdminLayout.css';
import '../styles/admin-modal-layer.css';

const SIDEBAR_COLLAPSED_KEY = 'adminSidebarCollapsed';

function readSidebarCollapsed(): boolean {
  try {
    return window.localStorage?.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    typeof window !== 'undefined' ? readSidebarCollapsed() : false,
  );
  const location = useLocation();

  React.useEffect(() => {
    try {
      window.localStorage?.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {}
  }, [sidebarCollapsed]);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((c) => !c);
  }, []);

  React.useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    if (sidebarOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const handleMenuClick = useCallback(() => {
    setSidebarOpen((o) => !o);
  }, []);

  const handleSidebarNavigate = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const currentPath = useMemo(() => {
    return location.pathname.replace(/^\/admin\/?/, '') || 'system-dashboard';
  }, [location.pathname]);

  return (
    <div
      className={[
        'admin-layout',
        sidebarOpen ? 'admin-layout-sidebar-open' : '',
        sidebarCollapsed ? 'admin-layout--sidebar-collapsed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ImpersonationBanner />
      <Topbar sidebarOpen={sidebarOpen} onMenuClick={handleMenuClick} />
      <div className="admin-layout-overlay" aria-hidden={!sidebarOpen} onClick={handleSidebarNavigate} />
      <div className="admin-layout-body">
        <Sidebar
          onNavigate={handleSidebarNavigate}
          currentPath={currentPath}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={toggleSidebarCollapsed}
        />
        <main className="admin-layout-main">
          <div className="admin-layout-bg" />
          <div className="admin-layout-content">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
